import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SPECIAL_CATEGORIES } from "./taxConfig";

const num = (n) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString("en-MY", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return v < 0 ? `(${s})` : s;
};
// whole-ringgit, but blank/zero shown as a dash
const dash = (n) => (n === "" || n === undefined || n === null || Number(n) === 0 ? "-" : num(n));
const num2 = (n) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

const MARGIN = 40;

function drawHeader(doc, r, sch) {
  const h = r.header || {};
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFontSize(9);
  doc.setTextColor(20);
  doc.setFont("helvetica", "normal");
  const L = MARGIN;
  const Rx = pageW - MARGIN;
  // Right-hand column: left-aligned block with aligned colons
  const rightX = doc.internal.pageSize.getWidth() - MARGIN - 150;
  const colonX = rightX + 55;
  let y = 40;
  doc.setFont("helvetica", "bold");
  doc.text(`NAME`, L, y);
  doc.text(`: ${h.companyName || ""}`, L + 90, y);
  doc.setFont("helvetica", "normal");
  doc.text(sch || "TCOM", Rx, y, { align: "right" });
  y += 13;
  doc.text(`TAX FILE NO.`, L, y); doc.text(`: ${h.taxFileNo || ""}`, L + 90, y);
  doc.text(`REF NO.`, rightX, y); doc.text(`: ${h.refNo || ""}`, colonX, y);
  y += 13;
  doc.text(`BASIS PERIOD`, L, y); doc.text(`: ${h.basisFrom || ""}  -  ${h.basisTo || ""}`, L + 90, y);
  doc.text(`CO. NO.`, rightX, y); doc.text(`: ${h.coNo || ""}`, colonX, y);
  y += 13;
  doc.text(`YEAR OF ASSESSMENT`, L, y); doc.text(`: ${r.ya}`, L + 110, y);
  doc.text(`B/CODE`, rightX, y); doc.text(`: ${h.bCode || ""}`, colonX, y);
  y += 16;
  doc.setDrawColor(120);
  doc.setLineWidth(0.6);
  doc.line(L, y, Rx, y);
  return y + 14;
}

export const downloadFullReportPdf = (r) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const bCode = r.header?.bCode || "";

  // Every real disallowable entry gets a NOTE number (listed in Notes to Accounts)
  const disList = r.disallowables || [];
  const disReal = disList.filter((d) => (d.description || "").trim());
  let _n = 0;
  const noteNumById = new Map();
  disReal.forEach((d) => noteNumById.set(d, ++_n));
  const noteRef = (d) => (noteNumById.has(d) ? `NOTE ${noteNumById.get(d)}` : "");
  const shownDis = disReal.filter((d) => Number(d.disallowed || 0) !== 0);

  // ---- Clean monochrome palette (simple / professional / tidy) ----
  const INK = [33, 43, 54];        // primary text
  const MUTED = [110, 122, 138];   // secondary text
  const HAIR = [140, 150, 163];    // total rule lines
  const LINE = [216, 221, 228];    // light separators
  const HEAD_BG = [239, 241, 244]; // table header fill
  const BRAND = HAIR;              // (rule colour alias, kept for existing refs)
  const BRAND_DARK = INK;         // section heading text
  const TINT = [246, 247, 249];   // section / group heading fill

  // Body starts below the clean repeating header (drawn in didDrawPage on every page)
  const CONTENT_TOP = 98;
  const drawChrome = (title, label) => {
    const pw = doc.internal.pageSize.getWidth();
    const h = r.header || {};
    // Company + registration line (left)
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text((h.companyName || "").toUpperCase(), MARGIN, 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text(`Tax File No: ${h.taxFileNo || "-"}     Co. No: ${h.coNo || "-"}     Ref No: ${h.refNo || "-"}     B/Code: ${h.bCode || "-"}`, MARGIN, 54);
    // YA + basis period (right)
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    doc.text(`YEAR OF ASSESSMENT ${r.ya}`, pw - MARGIN, 40, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text(`Basis Period: ${h.basisFrom || "-"}  \u2013  ${h.basisTo || "-"}`, pw - MARGIN, 54, { align: "right" });
    // primary rule
    doc.setDrawColor(...INK); doc.setLineWidth(1); doc.line(MARGIN, 61, pw - MARGIN, 61);
    // section title + page marker
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(10.5);
    doc.text(title, MARGIN, 78);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
    doc.text(`${label}  \u2022  Page ${doc.getNumberOfPages()}`, pw - MARGIN, 78, { align: "right" });
    doc.setDrawColor(...LINE); doc.setLineWidth(0.5); doc.line(MARGIN, 84, pw - MARGIN, 84);
    doc.setTextColor(...INK);
  };

  // A single computation page buffer (Description | Sch,wks,Notes | RM inner | RM outer)
  const mkPage = () => {
    const rows = [], bold = new Set(), rule = new Set(), ruleBelow = new Set(), ruleBelowInner = new Set(), ruleBelowOuter = new Set(), ruleAboveOuter = new Set(), ruleBelow2 = new Set(), ruleBelow2Outer = new Set();
    const fmt = (v) => (v === null || v === undefined ? "" : (typeof v === "string" ? v : num(v)));
    const push = (label, ref, inner, outer, isBold, indent) => {
      rows.push([(indent ? "        " : "") + label, ref || "", fmt(inner), fmt(outer)]);
      if (isBold) bold.add(rows.length - 1);
      return rows.length - 1;
    };
    const ruleAt = (i) => rule.add(i);
    const ruleBelowAt = (i) => ruleBelow.add(i);
    const ruleBelowInnerAt = (i) => ruleBelowInner.add(i);
    const ruleBelowOuterAt = (i) => ruleBelowOuter.add(i);
    const ruleAboveOuterAt = (i) => ruleAboveOuter.add(i);
    const ruleBelow2At = (i) => ruleBelow2.add(i);
    const ruleBelow2OuterAt = (i) => ruleBelow2Outer.add(i);
    return { rows, bold, rule, ruleBelow, ruleBelowInner, ruleBelowOuter, ruleAboveOuter, ruleBelow2, ruleBelow2Outer, push, ruleAt, ruleBelowAt, ruleBelowInnerAt, ruleBelowOuterAt, ruleAboveOuterAt, ruleBelow2At, ruleBelow2OuterAt };
  };

  const renderComputation = (title, buf) => {
    autoTable(doc, {
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      head: [["Description", "Sch, wks, Notes", "RM", "RM"]],
      body: buf.rows,
      theme: "plain",
      rowPageBreak: "avoid",
      styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 6, right: 6 }, textColor: INK },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold", fontSize: 8.5 },
      columnStyles: {
        0: { cellWidth: 246 },
        1: { cellWidth: 90, halign: "left" },
        2: { cellWidth: 82, halign: "right" },
        3: { cellWidth: 82, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head") { data.cell.styles.halign = data.column.index >= 2 ? "right" : "left"; return; }
        const row = buf.rows[data.row.index] || [];
        const isBold = buf.bold.has(data.row.index);
        const hasAmt = (row[2] != null && row[2] !== "") || (row[3] != null && row[3] !== "");
        if (isBold && !hasAmt) { data.cell.styles.fillColor = TINT; data.cell.styles.textColor = BRAND_DARK; data.cell.styles.fontStyle = "bold"; }
        else if (isBold) { data.cell.styles.fontStyle = "bold"; data.cell.styles.textColor = INK; }
      },
      didDrawCell: (data) => {
        if (
          data.section === "body" &&
          (data.column.index === 2 || data.column.index === 3) &&
          (data.cell.text || []).join("") !== ""
        ) {
          const d = data.doc;
          if (buf.rule.has(data.row.index)) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            d.line(data.cell.x + 3, data.cell.y, data.cell.x + data.cell.width - 3, data.cell.y);
          }
          if (buf.ruleBelow.has(data.row.index)) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            const yb = data.cell.y + data.cell.height;
            d.line(data.cell.x + 3, yb, data.cell.x + data.cell.width - 3, yb);
          }
          if (buf.ruleBelowInner.has(data.row.index) && data.column.index === 2) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            const yb = data.cell.y + data.cell.height;
            d.line(data.cell.x + 3, yb, data.cell.x + data.cell.width - 3, yb);
          }
          if (buf.ruleBelowOuter.has(data.row.index) && data.column.index === 3) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            const yb = data.cell.y + data.cell.height;
            d.line(data.cell.x + 3, yb, data.cell.x + data.cell.width - 3, yb);
          }
          if (buf.ruleAboveOuter.has(data.row.index) && data.column.index === 3) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            d.line(data.cell.x + 3, data.cell.y, data.cell.x + data.cell.width - 3, data.cell.y);
          }
          if (buf.ruleBelow2.has(data.row.index)) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            const yb = data.cell.y + data.cell.height;
            d.line(data.cell.x + 3, yb - 3, data.cell.x + data.cell.width - 3, yb - 3);
            d.line(data.cell.x + 3, yb - 1, data.cell.x + data.cell.width - 3, yb - 1);
          }
          if (buf.ruleBelow2Outer.has(data.row.index) && data.column.index === 3) {
            d.setDrawColor(...BRAND); d.setLineWidth(0.6);
            const yb = data.cell.y + data.cell.height;
            d.line(data.cell.x + 3, yb - 3, data.cell.x + data.cell.width - 3, yb - 3);
            d.line(data.cell.x + 3, yb - 1, data.cell.x + data.cell.width - 3, yb - 1);
          }
        }
      },
      didDrawPage: () => drawChrome(title, "TCOM"),
    });
    return doc.lastAutoTable.finalY;
  };

  // ===== PAGE 1 — Business income -> Adjusted income/loss =====
  const p1 = mkPage();
  p1.push("BUSINESS INCOME 1", "", null, null, true);
  p1.push("Net profit/(loss) as per accounts", "A/C", null, r.netProfit);
  p1.push("Less: Non-business income", "", null, null, true);
  p1.ruleBelowAt(p1.push("Other income", "A/C", r.otherIncomeTotal, r.otherIncomeTotal, false, true));
  p1.push("BUSINESS INCOME/(LOSS)", "", null, r.businessIncomeLoss, true);
  if (r.interestRestriction) p1.push("Add: Interest restriction under Sec.33(2)", "", null, r.interestRestriction, true);
  p1.push("Add: Disallowable expenses under Sec.39(1)", "", null, null, true);
  shownDis.forEach((d) => p1.push(d.description, noteRef(d), null, d.disallowed, false, true));
  p1.ruleAt(p1.push("", "", null, r.totalDisallowed));
  p1.push("Less: Allowance & deductions (S.111 / S.132 / S.157)", "", null, null, true);
  let _sdLast = -1;
  (r.specialDeductions || []).filter((s) => Number(s.amount || 0) !== 0).forEach((s) => { _sdLast = p1.push(s.label, "", s.amount, null, false, true); });
  if (_sdLast >= 0) p1.ruleBelowAt(_sdLast);
  p1.ruleAt(p1.push("", "", null, r.totalSpecialDeductions));
  p1.ruleAt(p1.push(r.adjustedIsLoss ? "ADJUSTED LOSS" : "ADJUSTED INCOME", "", null, r.adjustedIncome, true));
  renderComputation("TAX COMPUTATION", p1);

  // ===== PAGE 2 — Capital allowance -> Statutory income =====
  doc.addPage("a4", "portrait");
  const p2 = mkPage();
  p2.push("BUSINESS INCOME 1", "", null, null, true);
  p2.push("ADJUSTED INCOME", "", null, r.adjustedIsLoss ? 0 : r.adjustedIncome, true);
  if (r.balancingCharge) p2.push("Add: Balancing charge", "", null, r.balancingCharge);
  p2.push("Less: Capital Allowance", "", null, null, true);
  p2.push("Balance brought forward", "", r.capitalAllowanceBf, null, false, true);
  p2.push("Current year", "", r.capitalAllowanceCurrent, null, false, true);
  p2.ruleAt(p2.push("", "", (Number(r.capitalAllowanceBf || 0) + Number(r.capitalAllowanceCurrent || 0)), null));
  p2.ruleBelowAt(p2.push("Amount absorbed this year", "", r.capitalAllowanceAbsorbed, r.capitalAllowanceAbsorbed, false, true));
  p2.ruleBelow2At(p2.push("Balance carried forward", "", r.capitalAllowanceCf, null, false, true));
  const _siIdx = p2.push("STATUTORY INCOME", "", null, r.statutoryIncome, true);
  p2.ruleBelow2At(_siIdx);
  renderComputation("TAX COMPUTATION", p2);

  // ===== PAGE 3 — Aggregate -> Tax payable + signature =====
  doc.addPage("a4", "portrait");
  const p3 = mkPage();
  p3.push("BUSINESS INCOME", "", null, null, true);
  p3.push(`Statutory Income 1 (Business code ${bCode})`, "", null, r.statutoryIncome);
  p3.ruleAt(p3.push("AGGREGATE STATUTORY BUSINESS INCOME", "", null, r.aggregateStatutoryBusinessIncome, true));
  if (r.unabsorbedLossBf > 0 || r.lossBfAbsorbed > 0 || r.cyLossCf > 0 || r.unabsorbedLossCf > 0) {
    p3.push("Less: UNABSORBED LOSSES", "", null, null, true);
    p3.push("Balance brought forward", "", r.unabsorbedLossBf, null, false, true);
    const _absIdx = p3.push("Amount absorbed this year", "", r.lossBfAbsorbed, r.lossBfAbsorbed, false, true);
    p3.ruleBelowInnerAt(_absIdx);
    p3.ruleBelowOuterAt(_absIdx);
    p3.push("", "", (Number(r.unabsorbedLossBf || 0) - Number(r.lossBfAbsorbed || 0)), null, false, true);
    if (r.cyLossCf > 0) p3.ruleBelowAt(p3.push("Balance transferred from current year loss", "", r.cyLossCf, null, false, true));
    p3.ruleBelow2At(p3.push("Balance carried forward", "", r.unabsorbedLossCf, null, false, true));
  }
  p3.push("Add: Other statutory income", "", null, null, true);
  (r.otherStatutoryIncome || []).filter((x) => (x.label || "").trim() || Number(x.amount || 0)).forEach((x) => {
    p3.push(x.label || "Other income", "", null, Number(x.amount || 0), false, true);
  });
  p3.ruleAt(p3.push("AGGREGATE INCOME", "", null, r.aggregateIncome, true));
  if (r.adjustedIsLoss) {
    p3.push("Less: Current year loss", "", null, null, true);
    p3.push("Loss incurred", "", r.adjustedLoss, null, false, true);
    p3.ruleBelowInnerAt(p3.push("Amount absorbed", "", r.cyLossAbsorbed, r.cyLossAbsorbed, false, true));
    p3.ruleBelow2At(p3.push("Balance transferred to unabsorbed losses", "", r.cyLossCf, null, false, true));
  } else if (r.cyLossAbsorbed > 0) {
    p3.push("Less: Current year loss absorbed", "", null, r.cyLossAbsorbed);
  }
  const _ciIdx = p3.push("CHARGEABLE INCOME", "", null, r.chargeableIncome, true);
  p3.ruleAt(_ciIdx);
  p3.ruleBelow2At(_ciIdx);
  p3.push("TAX PAYABLE", "", null, null, true);
  (r.taxBreakdown || []).forEach((b) => p3.push(`${b.band} @ ${b.rate}%`, "", null, num2(b.amount), false, true));
  p3.ruleAt(p3.push("Gross tax", "", null, num2(r.grossTax), true));
  if (r.section110) p3.push("Less: Section 110 set off", "", num2(r.section110), null, false, true);
  if (r.section132relief) p3.push("Less: Section 132/133 relief", "", num2(r.section132relief), null, false, true);
  p3.ruleAt(p3.push("Tax charged", "", null, num2(r.taxAfterSetoff), true));
  p3.push("Less: Actual tax paid via CP204 instalment", "", null, num2(r.cp204Paid), false, true);
  const _balIdx = p3.push(r.balance < 0 ? "Tax over paid and refundable" : "Balance of tax payable", "", null, num2(r.balance), true);
  p3.ruleAt(_balIdx);
  p3.ruleBelow2At(_balIdx);
  const p3end = renderComputation("TAX COMPUTATION", p3);

  // Director signature — bottom-right of the last computation page
  const pw3 = doc.internal.pageSize.getWidth();
  const fy = Math.min(p3end + 44, doc.internal.pageSize.getHeight() - 70);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(20);
  doc.text("DIRECTOR / PERSON IN CHARGE", pw3 - MARGIN, fy, { align: "right" });
  const sigW = doc.getTextWidth("DIRECTOR / PERSON IN CHARGE");
  doc.setDrawColor(120); doc.setLineWidth(0.6);
  doc.line(pw3 - MARGIN - sigW, fy + 42, pw3 - MARGIN, fy + 42);

  // ---------- SCHEDULE OF CAPITAL ALLOWANCE (LANDSCAPE) ----------
  if ((r.assets || []).length) {
    doc.addPage("a4", "landscape");
    const assets = r.assets || [];

    // Group assets by category, preserving first-seen order
    const groups = [];
    const gidx = new Map();
    assets.forEach((a) => {
      const key = a.category || "OTHER";
      if (!gidx.has(key)) { gidx.set(key, groups.length); groups.push({ key, items: [] }); }
      groups[gidx.get(key)].items.push(a);
    });

    const numCols = ["originalCost", "qe", "costBf", "addition", "deletion", "costCf", "rvBf", "ia", "aa", "rvCf", "disposalValue", "ba", "bc"];
    const zero = () => Object.fromEntries(numCols.map((k) => [k, 0]));
    const grand = zero();
    const caBody = [];
    const caRuleRows = new Set();
    const caRuleBelowRows = new Set();
    const rowOf = (a) => [
      (a.description || "") + (a.notional ? " @" : ""),
      String(a.yearAcq || ""),
      a.rateIA ? String(a.rateIA) : "0",
      a.rateAA ? String(a.rateAA) : "0",
      num(a.originalCost), num(a.qe), num(a.costBf), num(a.addition), num(a.deletion), num(a.costCf),
      num(a.rvBf), num(a.ia), num(a.aa), num(a.rvCf), num(a.disposalValue), num(a.ba), num(a.bc),
    ];
    groups.forEach((g) => {
      caBody.push([{ content: g.key, colSpan: 17, styles: { fontStyle: "bold", fillColor: TINT, textColor: BRAND_DARK } }]);
      const sub = zero();
      g.items.forEach((a) => {
        caBody.push(rowOf(a));
        numCols.forEach((k) => { sub[k] += Number(a[k] || 0); grand[k] += Number(a[k] || 0); });
      });
      caBody.push([
        "", "", "", "",
        num(sub.originalCost), num(sub.qe), num(sub.costBf), num(sub.addition), num(sub.deletion), num(sub.costCf),
        num(sub.rvBf), num(sub.ia), num(sub.aa), num(sub.rvCf), num(sub.disposalValue), num(sub.ba), num(sub.bc),
      ]);
      caRuleRows.add(caBody.length - 1);
      caRuleBelowRows.add(caBody.length - 1);
    });
    caBody.push([
      { content: "TOTAL", colSpan: 4, styles: { fontStyle: "bold" } },
      { content: num(grand.originalCost), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.qe), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.costBf), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.addition), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.deletion), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.costCf), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.rvBf), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.ia), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.aa), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.rvCf), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.disposalValue), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.ba), styles: { fontStyle: "bold", halign: "right" } },
      { content: num(grand.bc), styles: { fontStyle: "bold", halign: "right" } },
    ]);
    caRuleRows.add(caBody.length - 1);

    autoTable(doc, {
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      rowPageBreak: "avoid",
      head: [[
        "DESCRIPTION OF ASSETS", "Year\nAcq'd", "IA\n%", "AA\n%",
        "Original\nCost RM", "QE\nRM", "Cost\nb/f RM", "Addition\nRM", "Deletion\nRM", "Cost\nc/f RM",
        "R.V.\nb/f RM", "Initial\nRM", "Annual\nRM", "R.V.\nc/f RM", "Disposal\nRM", "BA\nRM", "BC\nRM",
      ]],
      body: caBody,
      theme: "plain",
      styles: { fontSize: 7, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: INK, overflow: "linebreak" },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold", fontSize: 6.5 },
      columnStyles: {
        0: { cellWidth: 118 }, 1: { cellWidth: 26, halign: "center" }, 2: { cellWidth: 20, halign: "center" }, 3: { cellWidth: 20, halign: "center" },
        4: { cellWidth: 48, halign: "right" }, 5: { cellWidth: 44, halign: "right" }, 6: { cellWidth: 44, halign: "right" }, 7: { cellWidth: 42, halign: "right" }, 8: { cellWidth: 42, halign: "right" }, 9: { cellWidth: 46, halign: "right" },
        10: { cellWidth: 42, halign: "right" }, 11: { cellWidth: 40, halign: "right" }, 12: { cellWidth: 40, halign: "right" }, 13: { cellWidth: 42, halign: "right" }, 14: { cellWidth: 46, halign: "right" }, 15: { cellWidth: 36, halign: "right" }, 16: { cellWidth: 36, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.fontStyle = "bold"; data.cell.styles.textColor = 20; data.cell.styles.fontSize = 6.5;
          data.cell.styles.halign = data.column.index === 0 ? "left" : (data.column.index <= 3 ? "center" : "right");
        }
      },
      didDrawCell: (data) => {
        if (data.section === "body" && caRuleRows.has(data.row.index) && data.column.index === 4) {
          const d = data.doc; d.setDrawColor(60); d.setLineWidth(0.5);
          const pwCA = d.internal.pageSize.getWidth();
          d.line(data.cell.x, data.cell.y, pwCA - MARGIN, data.cell.y);
        }
        if (data.section === "body" && caRuleBelowRows.has(data.row.index) && data.column.index === 4) {
          const d = data.doc; d.setDrawColor(60); d.setLineWidth(0.5);
          const pwCA = d.internal.pageSize.getWidth();
          const yb = data.cell.y + data.cell.height - 1;
          d.line(data.cell.x, yb, pwCA - MARGIN, yb);
        }
      },
      didDrawPage: () => drawChrome("SCHEDULE OF CAPITAL ALLOWANCE", "SCH CA"),
    });

    // ---- SUMMARY block (RM claimed | NA notional | NET total) ----
    // Use the engine's rounded COLUMN totals so the summary matches a round-the-total working
    // (per-asset rows are whole-ringgit and may differ from the column total by a ringgit or two).
    const cs = r.caSummary || {};
    const rm_ia = Number(cs.iaClaimed || 0);
    const rm_aa = Number(cs.aaClaimed || 0);
    const na_ia = Number(cs.iaNotional || 0);
    const na_aa = Number(cs.aaNotional || 0);
    const sBa = Number(cs.ba || 0);
    const sBc = Number(cs.bc || 0);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 22,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      head: [["SUMMARY", "RM", "NA - RM", "NET - RM"]],
      body: [
        ["Initial allowance", num(rm_ia), num(na_ia), num(rm_ia + na_ia)],
        ["Annual allowance", num(rm_aa), num(na_aa), num(rm_aa + na_aa)],
        [
          "",
          { content: num(rm_ia + rm_aa), styles: { fontStyle: "bold", halign: "right" }, _r: true },
          { content: num(na_ia + na_aa), styles: { fontStyle: "bold", halign: "right" }, _r: true },
          { content: num(rm_ia + rm_aa + na_ia + na_aa), styles: { fontStyle: "bold", halign: "right" }, _r: true },
        ],
        ["Balancing allowance", num(sBa), "0", num(sBa)],
        ["Balancing charge", num(sBc), "0", num(sBc)],
      ],
      theme: "plain",
      styles: { fontSize: 7.5, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: INK },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 120 }, 1: { cellWidth: 70, halign: "right" }, 2: { cellWidth: 70, halign: "right" }, 3: { cellWidth: 70, halign: "right" } },
      didParseCell: (data) => {
        if (data.section === "head") { data.cell.styles.halign = data.column.index === 0 ? "left" : "right"; }
      },
      didDrawCell: (data) => {
        if (data.section === "body" && data.cell.raw && data.cell.raw._r && data.column.index === 1) {
          const d = data.doc; d.setDrawColor(...BRAND); d.setLineWidth(0.6);
          const x1 = data.cell.x;
          const x2 = data.cell.x + 210;
          d.line(x1, data.cell.y, x2, data.cell.y);
          const yb = data.cell.y + data.cell.height;
          d.line(x1, yb, x2, yb);
        }
      },
      didDrawPage: () => drawChrome("SCHEDULE OF CAPITAL ALLOWANCE", "SCH CA"),
    });
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(90);
    doc.text("@ Notional Allowance applied,  **Controlled In,  ***Controlled Out,  ****Industrial Building Allowance", MARGIN, doc.lastAutoTable.finalY + 14);
    doc.setTextColor(20);
  }

  // ---------- NOTES TO ACCOUNTS ----------
  if (disReal.length) {
    doc.addPage("a4", "portrait");
    const notesBody = [];
    disReal.forEach((d) => {
      const ref = noteNumById.get(d);
      notesBody.push([{ content: `NOTE ${ref} - ${(d.description || "").toUpperCase()}`, colSpan: 5, styles: { fontStyle: "bold", fillColor: TINT, textColor: BRAND_DARK } }]);
      const expl = (d.notes && d.notes.length)
        ? d.notes
        : [{ amount: d.amount, text: d.note || d.remark || d.description, treatment: Number(d.disallowed || 0) !== 0 ? "disallowed" : "allowed" }];
      let sumAmt = 0, sumDis = 0;
      expl.forEach((e) => {
        const amt = Number(e.amount || 0);
        const dis = e.treatment === "allowed" ? 0 : amt;
        sumAmt += amt; sumDis += dis;
        notesBody.push(["", (d.payee || d.description || "").toUpperCase(), e.text || "", num(amt), num(dis)]);
      });
      notesBody.push([
        "", "", "",
        { content: num(sumAmt), styles: { fontStyle: "bold", halign: "right" }, _rule: true, _ruleBelow: true },
        { content: num(sumDis), styles: { fontStyle: "bold", halign: "right" }, _rule: true, _ruleBelow: true },
      ]);
      notesBody.push([{ content: `Remark:  ${d.remark || d.description || ""}`, colSpan: 5, styles: { fontStyle: "italic", textColor: 90 } }]);
    });
    autoTable(doc, {
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      rowPageBreak: "avoid",
      head: [["DATE", "PAYEE / PAYOR", "DESCRIPTION OF EXPENSES / INCOME", "AMOUNT\nRM", "DISALLOWED\nRM"]],
      body: notesBody,
      theme: "plain",
      styles: { fontSize: 8, cellPadding: { top: 2.8, bottom: 2.8, left: 4, right: 4 }, textColor: INK, overflow: "linebreak" },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold", fontSize: 7.5 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 120 },
        2: { cellWidth: "auto" },
        3: { cellWidth: 70, halign: "right" },
        4: { cellWidth: 70, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = data.column.index >= 3 ? "right" : "left";
        }
      },
      didDrawCell: (data) => {
        if (data.section === "body" && data.cell.raw && data.cell.raw._rule) {
          const d = data.doc; d.setDrawColor(...BRAND); d.setLineWidth(0.6);
          d.line(data.cell.x + 2, data.cell.y, data.cell.x + data.cell.width - 2, data.cell.y);
        }
        if (data.section === "body" && data.cell.raw && data.cell.raw._ruleBelow) {
          const d = data.doc; d.setDrawColor(...BRAND); d.setLineWidth(0.6);
          const yb = data.cell.y + data.cell.height;
          d.line(data.cell.x + 2, yb, data.cell.x + data.cell.width - 2, yb);
        }
      },
      didDrawPage: () => drawChrome("NOTES TO ACCOUNTS", "SCH NTA"),
    });
  }

  // ---------- SPECIAL DEDUCTIONS DETAIL SCHEDULE ----------
  const specialSchedule = SPECIAL_CATEGORIES.map((cat) => {
    const lines = [];
    (r.disallowables || []).forEach((d) => {
      const subs = (d.notes || []).filter((s) => s.treatment === "disallowed" && s.specialCategory === cat.key);
      if (subs.length) {
        subs.forEach((s) => lines.push({ desc: d.description, text: s.text || "", amount: Number(s.amount || 0) }));
      } else if (d.treatment === "disallowed" && d.specialCategory === cat.key && Number(d.disallowed || 0) !== 0) {
        lines.push({ desc: d.description, text: d.remark || d.note || "", amount: Number(d.disallowed || 0) });
      }
    });
    return { cat, lines, total: lines.reduce((s, l) => s + l.amount, 0) };
  }).filter((g) => g.lines.length);

  if (specialSchedule.length) {
    doc.addPage("a4", "portrait");
    const sdBody = [];
    let sdGrand = 0;
    specialSchedule.forEach((g) => {
      sdGrand += g.total;
      sdBody.push([{ content: g.cat.sdLabel.toUpperCase(), colSpan: 3, styles: { fontStyle: "bold", fillColor: TINT, textColor: BRAND_DARK } }]);
      g.lines.forEach((l) => sdBody.push([l.desc || "", l.text || "", dash(l.amount)]));
      sdBody.push(["", { content: "Total", styles: { fontStyle: "bold" } }, { content: dash(g.total), styles: { fontStyle: "bold", halign: "right" } }]);
    });
    sdBody.push([
      { content: "TOTAL SPECIAL DEDUCTIONS", colSpan: 2, styles: { fontStyle: "bold", fillColor: INK, textColor: [255, 255, 255] } },
      { content: dash(sdGrand), styles: { fontStyle: "bold", halign: "right", fillColor: INK, textColor: [255, 255, 255] } },
    ]);
    autoTable(doc, {
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      rowPageBreak: "avoid",
      head: [["Expense (added back)", "Description", "Amount RM"]],
      body: sdBody,
      theme: "grid",
      styles: { fontSize: 8.5, cellPadding: 4, overflow: "linebreak", lineColor: [223, 232, 242], textColor: INK },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 180 }, 1: { cellWidth: "auto" }, 2: { halign: "right", cellWidth: 90 } },
      didParseCell: (data) => { if (data.column.index === 2) data.cell.styles.halign = "right"; },
      didDrawPage: () => drawChrome("SPECIAL DEDUCTIONS SCHEDULE (S.111 / S.132 / S.157)", "SCH SD"),
    });
  }

  // ---------- OTHER STATUTORY INCOME SCHEDULE ----------
  const osiRows = (r.otherStatutoryIncome || []).filter((x) => (x.label || "").trim() || Number(x.amount || 0));
  if (osiRows.length) {
    doc.addPage("a4", "portrait");
    const osiBody = osiRows.map((x) => [x.label || "", num2(Number(x.amount || 0))]);
    osiBody.push([
      { content: "TOTAL OTHER STATUTORY INCOME", styles: { fontStyle: "bold", fillColor: INK, textColor: [255, 255, 255] } },
      { content: num2(r.otherStatutoryIncomeTotal), styles: { fontStyle: "bold", halign: "right", fillColor: INK, textColor: [255, 255, 255] } },
    ]);
    autoTable(doc, {
      startY: CONTENT_TOP,
      margin: { top: CONTENT_TOP, left: MARGIN, right: MARGIN },
      rowPageBreak: "avoid",
      head: [["Source / Description", "Amount RM"]],
      body: osiBody,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 5, overflow: "linebreak", lineColor: [223, 232, 242], textColor: INK },
      headStyles: { fillColor: HEAD_BG, textColor: INK, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: "auto" }, 1: { halign: "right", cellWidth: 120 } },
      didParseCell: (data) => { if (data.column.index === 1) data.cell.styles.halign = "right"; },
      didDrawPage: () => drawChrome("SCHEDULE OF OTHER STATUTORY INCOME", "SCH OSI"),
    });
  }

  const safe = (r.header?.companyName || "computation").replace(/[^a-z0-9]+/gi, "_");
  doc.save(`TaxComputation_${safe}_YA${r.ya}.pdf`);
};

// ---------- FORM C — full 7-page replica of the official LHDN Borang C ---------- //
export const downloadFormCPdf = (r, p = {}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const h = r.header || {};
  const ya = r.ya;
  const yn = (b) => (b ? "Ya (Yes)" : "Tidak (No)");
  const grey = [107, 107, 107];

  const boldTable = (cfg) => autoTable(doc, {
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: grey, textColor: 255, fontStyle: "bold" },
    margin: { left: MARGIN, right: MARGIN },
    ...cfg,
    didParseCell: (data) => {
      if (data.section === "body" && cfg.boldRows && cfg.boldRows.has(data.row.index)) data.cell.styles.fontStyle = "bold";
      if (cfg.rightCol !== undefined && data.column.index === cfg.rightCol) data.cell.styles.halign = "right";
      if (cfg.didParseCell) cfg.didParseCell(data);
    },
  });

  const pageTitle = (txt) => {
    let yy = drawHeader(doc, r, "BORANG C");
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5);
    doc.text(txt, MARGIN, yy);
    doc.setFont("helvetica", "normal");
    return yy + 8;
  };

  // Derived Form C figures
  const aggStatBiz = Number(r.aggregateStatutoryBusinessIncome || 0);
  const lossBf = Number(r.lossBfAbsorbed || 0);
  const afterLossBf = aggStatBiz - lossBf;
  const otherInc = Number(r.otherStatutoryIncomeTotal || 0);
  const aggIncome = Number(r.aggregateIncome || 0);
  const cyLoss = Number(r.cyLossAbsorbed || 0);
  const determinedAgg = aggIncome - cyLoss;
  const chargeable = Number(r.chargeableIncome || 0);
  const grossTax = Number(r.grossTax || 0);
  const s110 = Number(r.section110 || 0);
  const s132 = Number(r.section132relief || 0);
  const taxPayable = Number(r.taxAfterSetoff || 0);
  const cp204 = Number(r.cp204Paid || 0);
  const balance = Number(r.balance || 0);
  const ca = r.caSummary || {};

  const compName = p.name || h.companyName || "";
  const director = p.personSign || "";
  const directorId = p.nric || "";
  const directorPos = p.position || "DIRECTOR";

  // ===== PAGE 1 — Part A + Part B =====
  let y = pageTitle(`BORANG C — PULANGAN CUKAI PENDAPATAN SYARIKAT  (TAHUN TAKSIRAN ${ya})`);
  doc.setFontSize(8); doc.setTextColor(90);
  doc.text(`Return Form of a Company (Form C) — Year of Assessment ${ya}`, MARGIN, y);
  doc.setTextColor(20);

  boldTable({
    startY: y + 8,
    head: [[{ content: "BAHAGIAN A: MAKLUMAT ASAS (PART A: BASIC INFORMATION)", colSpan: 2 }]],
    body: [
      ["Nama syarikat (Company name)", compName],
      ["No. Pengenalan Cukai / TIN (Tax reference no.)", h.taxFileNo || p.taxFileNo || ""],
      ["No. majikan (Employer's no. E)", p.employerE || ""],
      ["No. rujukan / pendaftaran (Registration no.)", h.coNo || p.companyNo || ""],
      ["Kod perniagaan (Business code)", h.bCode || p.businessCode || ""],
      ["Diperbadankan di Malaysia (Incorporated in Malaysia)", yn(p.incorporatedMalaysia !== false)],
      ["Tempoh perakaunan (Accounting period)", `${p.accFrom || ""}  -  ${p.accTo || ""}`],
      ["Tempoh asas (Basis period)", `${h.basisFrom || p.basisFrom || ""}  -  ${h.basisTo || p.basisTo || ""}`],
      ["Status perniagaan (Business status)", p.companyStatus || p.operationStatus || "In Operation"],
      ["Negara mastautin (Country of residence)", p.country || "Malaysia"],
      ["Syarikat SME — modal berbayar ≤ RM2.5j & pendapatan kasar ≤ RM50j (Sch.1 Part 1 para 2A)", yn(r.isSME)],
    ],
    columnStyles: { 0: { cellWidth: 360 }, 1: { cellWidth: "auto" } },
  });

  const bBold = new Set(); const bBody = [];
  const bAdd = (l, a, b) => { bBody.push([l, a === null ? "" : num(a)]); if (b) bBold.add(bBody.length - 1); };
  bAdd("Agregat pendapatan berkanun perniagaan & perkongsian (punca Malaysia)", aggStatBiz);
  bAdd("Agregat pendapatan berkanun perniagaan & perkongsian (punca luar Malaysia)", 0);
  bAdd("AGREGAT PENDAPATAN BERKANUN PERNIAGAAN & PERKONGSIAN", aggStatBiz, true);
  bAdd("Tolak: Rugi perniagaan bawa hadapan (Less: business loss b/f)", lossBf);
  bAdd("JUMLAH (Total)", afterLossBf, true);
  bAdd("Agregat pendapatan berkanun lain — punca Malaysia (dividen, faedah, sewa, dll.)", otherInc);
  bAdd("Agregat pendapatan berkanun lain — punca asing diterima di Malaysia", 0);
  bAdd("AGREGAT PENDAPATAN BERKANUN LAIN (Aggregate other statutory income)", otherInc, true);
  bAdd("PENDAPATAN AGREGAT (AGGREGATE INCOME)", aggIncome, true);
  bAdd("Tolak: Rugi perniagaan tahun semasa (Less: current year business loss)", cyLoss);
  bAdd("Tolak: Derma / hadiah / sumbangan diluluskan (Approved donations)", 0);
  bAdd("Tolak: Zakat perniagaan (Business zakat, max 2.5%)", 0);
  bAdd("PENDAPATAN AGREGAT DITENTUKAN (Determined aggregate income)", determinedAgg, true);
  bAdd("Tolak: Tuntutan kerugian Relif Kumpulan (Group relief)", 0);
  bAdd("JUMLAH PENDAPATAN (TOTAL INCOME)", determinedAgg, true);
  bAdd("Pendapatan perintis kena cukai (Pioneer income chargeable)", 0);
  bAdd("PENDAPATAN BERCUKAI (CHARGEABLE INCOME)", chargeable, true);
  boldTable({
    startY: doc.lastAutoTable.finalY + 10,
    head: [[{ content: "BAHAGIAN B: PENDAPATAN BERKANUN, JUMLAH PENDAPATAN & PENDAPATAN BERCUKAI", colSpan: 2 }], ["Perkara (Item)", "RM"]],
    body: bBody, boldRows: bBold, rightCol: 1,
    columnStyles: { 0: { cellWidth: 380 }, 1: { halign: "right", cellWidth: "auto" } },
  });

  // ===== PAGE 2 — Tax payable / refundable =====
  doc.addPage();
  let ty = pageTitle(`CUKAI KENA DIBAYAR / DIBAYAR BALIK — TAHUN TAKSIRAN ${ya}`);
  const tBold = new Set(); const tBody = [];
  const tAdd = (l, a, b, two) => { tBody.push([l, a === null ? "" : (two ? num2(a) : num(a))]); if (b) tBold.add(tBody.length - 1); };
  tAdd("JUMLAH PENDAPATAN BERCUKAI (Total chargeable income)", chargeable, true);
  (r.taxBreakdown || []).forEach((b) => tAdd(`   Pecahan: ${b.band} @ ${b.rate}%`, b.amount));
  tAdd("JUMLAH CUKAI PENDAPATAN DIKENAKAN (Total income tax imposed)", grossTax, true);
  tAdd("Tolak: Rebat seksyen 6D (sehingga RM20,000)", 0);
  tAdd("JUMLAH (Total)", grossTax, true);
  tAdd("Tolak: Tolakan cukai seksyen 110B", 0);
  tAdd("Tolak: Tolakan cukai seksyen 110 (lain-lain)", s110);
  tAdd("Tolak: Pelepasan cukai seksyen 132", s132);
  tAdd("Tolak: Pelepasan cukai seksyen 133", 0);
  tAdd(`CUKAI KENA DIBAYAR TAHUN TAKSIRAN ${ya}`, taxPayable, true);
  tAdd("Tolak: Bayaran ansuran seksyen 107C (CP204)", cp204);
  tAdd("Tolak: Bayaran perenggan 107A(1)(a)", 0);
  tBody.push([balance < 0 ? `CUKAI TERLEBIH BAYAR TAHUN TAKSIRAN ${ya}` : `BAKI CUKAI KENA DIBAYAR TAHUN TAKSIRAN ${ya}`, num2(Math.abs(balance))]);
  tBold.add(tBody.length - 1);
  boldTable({
    startY: ty + 10,
    head: [["Perkara (Item)", "RM"]],
    body: tBody, boldRows: tBold, rightCol: 1,
    columnStyles: { 0: { cellWidth: 380 }, 1: { halign: "right", cellWidth: "auto" } },
  });

  // ===== PAGE 3 — Capital allowance (Schedule 3), incentive, loss, remission =====
  doc.addPage();
  let cy = pageTitle("ELAUN MODAL JADUAL 3, TUNTUTAN INSENTIF, KERUGIAN & REMISI CUKAI");
  const caBody = (r.assets || []).map((a, i) => [
    String(i + 1), a.description || `Aset ${i + 1}`, num(a.ia + a.aa), "0",
  ]);
  caBody.push([
    { content: "JUMLAH (Total)", colSpan: 2, styles: { fontStyle: "bold" } },
    { content: num(ca.total || 0), styles: { fontStyle: "bold", halign: "right" } },
    { content: "0", styles: { fontStyle: "bold", halign: "right" } },
  ]);
  boldTable({
    startY: cy + 8,
    head: [[{ content: "MAKLUMAT ELAUN MODAL DAN KENAAN JADUAL 3", colSpan: 4 }], ["Bil.", "Pengenalan perniagaan / aset", "Elaun modal (E) RM", "Baki h/hadapan (G) RM"]],
    body: caBody, rightCol: 2,
    columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: "auto" }, 2: { halign: "right", cellWidth: 120 }, 3: { halign: "right", cellWidth: 120 } },
  });
  boldTable({
    startY: doc.lastAutoTable.finalY + 6,
    body: [
      ["Elaun modal b/f (Capital allowance b/f)", num(r.capitalAllowanceBf || 0)],
      ["Elaun modal semasa (Current year capital allowance)", num(r.capitalAllowanceCurrent || 0)],
      ["Elaun modal diserap (Absorbed)", num(r.capitalAllowanceAbsorbed || 0)],
      ["Baki elaun modal hantar hadapan (Balance c/f)", num(r.capitalAllowanceCf || 0)],
    ], rightCol: 1,
    columnStyles: { 0: { cellWidth: 380 }, 1: { halign: "right", cellWidth: "auto" } },
  });
  boldTable({
    startY: doc.lastAutoTable.finalY + 10,
    head: [[{ content: "TUNTUTAN INSENTIF (D1) / TUNTUTAN KERUGIAN / REMISI CUKAI", colSpan: 2 }]],
    body: [
      ["D1 Perbelanjaan khas / potongan dua kali (Special/double deductions)", num(r.totalSpecialDeductions || 0)],
      ["Rugi perniagaan hantar hadapan (Loss c/f)", num(r.unabsorbedLossCf || 0)],
      ["Remisi cukai perenggan 129(1)(b) (Tax remission)", "0"],
    ], rightCol: 1,
    columnStyles: { 0: { cellWidth: 380 }, 1: { halign: "right", cellWidth: "auto" } },
  });

  // ===== PAGE 4 — Company information =====
  doc.addPage();
  let py = pageTitle("MAKLUMAT SYARIKAT (COMPANY INFORMATION)");
  boldTable({
    startY: py + 8,
    head: [[{ content: "MAKLUMAT BAYARAN BALIK & PENGARAH", colSpan: 2 }]],
    body: [
      ["Kaedah bayaran balik lebihan cukai (Refund method)", "Melalui akaun sendiri di Malaysia"],
      ["Nama bank (Bank name)", ""],
      ["No. akaun bank (Bank account no.)", ""],
      ["Maklumat pengarah — Nama (Director name)", director],
      ["Pengarah — No. Pengenalan (Director ID / NRIC)", directorId],
    ],
    columnStyles: { 0: { cellWidth: 320 }, 1: { cellWidth: "auto" } },
  });
  boldTable({
    startY: doc.lastAutoTable.finalY + 8,
    head: [[{ content: "MAKLUMAT LANJUT SYARIKAT", colSpan: 2 }]],
    body: [
      ["Syarikat terkawal (Controlled company)", yn(false)],
      ["Syarikat Berkaitan Kerajaan / GLC", yn(false)],
      ["Syarikat disenaraikan di Bursa Malaysia (Listed)", yn(false)],
      ["Mempunyai syarikat induk (Has holding company)", yn(false)],
      ["Menjalankan transaksi terkawal (s.139 & 140A controlled transactions)", yn(false)],
      ["Tertakluk sekatan faedah s.140C (Interest restriction) — belanja faedah disekat", num(r.interestRestriction || 0)],
    ],
    columnStyles: { 0: { cellWidth: 360 }, 1: { cellWidth: "auto" } },
  });

  // ===== PAGE 5 — Other information + address =====
  doc.addPage();
  let oy = pageTitle("MAKLUMAT LAIN (OTHER INFORMATION) & ALAMAT");
  boldTable({
    startY: oy + 8,
    head: [[{ content: "MAKLUMAT LAIN", colSpan: 2 }]],
    body: [
      ["Bayaran tertakluk cukai pegangan s.107A/109/109B (Withholding tax)", yn(false)],
      ["Melupuskan aset modal di bawah ACP 1967 (Capital asset disposal)", yn(!!p.disposalCapital)],
      ["Melaporkan pelupusan aset modal (Reported to LHDNM)", yn(!!p.declaredCKM)],
      ["Melupuskan aset di bawah CKHT 1976 (RPGT disposal)", yn(!!p.disposalProperty)],
      ["Melaporkan pelupusan CKHT (Reported to LHDNM)", yn(!!p.declaredCKHT)],
      ["Menjalankan perniagaan e-Dagang (e-Commerce business)", yn(false)],
      ["Tertakluk Pelaporan Mengikut Negara / CbCR 2016", yn(false)],
    ],
    columnStyles: { 0: { cellWidth: 360 }, 1: { cellWidth: "auto" } },
  });
  boldTable({
    startY: doc.lastAutoTable.finalY + 8,
    head: [[{ content: "ALAMAT (ADDRESS)", colSpan: 2 }]],
    body: [
      ["Alamat surat-menyurat (Mailing address)", ""],
      ["Poskod (Postcode)", ""],
      ["Negeri (State)", ""],
    ],
    columnStyles: { 0: { cellWidth: 200 }, 1: { cellWidth: "auto" } },
  });

  // ===== PAGE 6 — Auditor, Tax agent, Declaration =====
  doc.addPage();
  let ay = pageTitle("MAKLUMAT JURUAUDIT, EJEN CUKAI & AKUAN");
  boldTable({
    startY: ay + 8,
    head: [[{ content: "MAKLUMAT JURUAUDIT (AUDITOR) & EJEN CUKAI (TAX AGENT)", colSpan: 2 }]],
    body: [
      ["Nama firma juruaudit (Audit firm name)", ""],
      ["No. Pengenalan Cukai firma (Firm TIN)", ""],
      ["Nama ejen cukai (Tax agent name)", ""],
      ["No. kelulusan ejen cukai (Tax agent approval no.)", ""],
    ],
    columnStyles: { 0: { cellWidth: 320 }, 1: { cellWidth: "auto" } },
  });
  boldTable({
    startY: doc.lastAutoTable.finalY + 8,
    head: [[{ content: "AKUAN (DECLARATION)", colSpan: 2 }]],
    body: [
      ["Saya (I)", director],
      ["No. Pengenalan (ID / NRIC)", directorId],
      ["Jawatan (Position)", directorPos],
      ["Tarikh (Date)", p.signDate || ""],
    ],
    columnStyles: { 0: { cellWidth: 200 }, 1: { cellWidth: "auto" } },
  });
  let dy = doc.lastAutoTable.finalY + 30;
  doc.setDrawColor(120);
  doc.line(MARGIN, dy, MARGIN + 200, dy);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(90);
  doc.text("Tandatangan pembayar cukai (Taxpayer's signature)", MARGIN, dy + 12);
  doc.setTextColor(20);

  // ===== PAGE 7 — Important reminders =====
  doc.addPage();
  let ry = pageTitle("PERINGATAN PENTING (IMPORTANT REMINDERS)");
  const reminders = [
    "1. Tarikh akhir pengemukaan Borang e-C dan bayaran cukai: dalam tempoh 7 bulan dari tarikh tutup perakaunan. (Due date: within 7 months from the accounting period end.)",
    "2. Syarikat hendaklah mengemukakan borang nyata secara elektronik [s.77A(1A)] dan melaporkan pendapatan berdasarkan penyata kewangan [s.77A(4)].",
    "3. Tindakan di bawah seksyen 112 ACP 1967 boleh dikenakan jika gagal mengemukakan borang nyata pada atau sebelum tarikh akhir.",
    "4. Kenaikan cukai 10% di bawah subseksyen 103(3) ACP 1967 dikenakan jika gagal membayar cukai/baki cukai pada atau sebelum tarikh akhir.",
    "5. Bayaran cukai boleh dibuat melalui ByrHASiL, bank komersial yang dilantik, atau kaunter Pos Malaysia menggunakan Nombor Bil.",
    "6. Seksyen 89 ACP 1967 — pertukaran alamat hendaklah dimaklumkan kepada LHDNM dalam tempoh 3 bulan melalui e-Kemaskini Syarikat.",
    "7. Untuk maklumat lanjut, hubungi HASiL Contact Centre: 03-8911 1000.",
  ];
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(30);
  let liney = ry + 16;
  reminders.forEach((t) => {
    const lines = doc.splitTextToSize(t, doc.internal.pageSize.getWidth() - MARGIN * 2);
    doc.text(lines, MARGIN, liney);
    liney += lines.length * 12 + 6;
  });

  // Footer page numbers
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(130);
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.text(`${compName}`, MARGIN, ph - 20);
    doc.text(`Muka surat ${i} / ${total}  (Page ${i} of ${total})`, pw - MARGIN, ph - 20, { align: "right" });
  }

  const safe = (compName || "formc").replace(/[^a-z0-9]+/gi, "_");
  doc.save(`FormC_${safe}_YA${ya}.pdf`);
};
